import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { MeasureBundleObject, NudgeObject } from '@grow-therapy-team/seedling-components';

import { Server } from '../apollo/types';
import { logger } from '../datadog/logger';
import { sortMeasureBundles } from './measures/utils';
import {
  patientShortIdToHasCompletedMeasureResponsesAtom,
  patientShortIdWithFetchedMeasuresAtom,
  selectedClientMeasuresData,
  selectedPatientNudgesAtom,
} from './state';

export type GetPatientMeasuresByPatientShortIdQueryData = {
  patientChart?: {
    __typename?: 'PatientObject';
    id: string;
    shortId: string;
    hasCompletedMeasureResponses: boolean | undefined;
    measureBundles: MeasureBundleObject[];
    nudges: NudgeObject[] | null;
  } | null;
};

export type GetPatientMeasuresByPatientShortIdVariables = {
  patientShortId?: string;
};

export const GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID = gql`
  query GetPatientMeasuresByPatientShortId($patientShortId: ID!) {
    patientChart(values: { patientShortId: $patientShortId }) {
      id
      shortId
      hasCompletedMeasureResponses
      measureBundles {
        measureBundleShortId
        ...MeasureBundlesList_MeasureBundleFields
        measureBundleItems {
          measureBundleItemId
          measureResponse {
            displayScore
          }
          measureSchema {
            measureSchemaShortId
            type
            ...MeasuresContent_MeasureSchemaFields
          }
        }
      }
      nudges {
        id
        nudgeSchemaId
        nudgeSchema {
          trigger
        }
      }
    }
  }
`;

const useSetPatientMeasuresDataCallback = () => {
  const setNudges = useSetAtom(selectedPatientNudgesAtom);
  const setMeasuresData = useSetAtom(selectedClientMeasuresData);
  const setPatientShortIdWithFetchedMeasures = useSetAtom(patientShortIdWithFetchedMeasuresAtom);
  const setPatientShortIdToHasCompletedMeasureResponses = useSetAtom(patientShortIdToHasCompletedMeasureResponsesAtom);
  return useCallback(
    (data: GetPatientMeasuresByPatientShortIdQueryData) => {
      const patientChart = data?.patientChart;
      if (!patientChart) return;
      setNudges(patientChart?.nudges ?? []);
      const sortedMeasureData = sortMeasureBundles(patientChart?.measureBundles ?? [], 'createdAt');
      const patientShortId = patientChart?.shortId;
      setPatientShortIdToHasCompletedMeasureResponses((prev) => ({
        ...prev,
        [patientShortId]: patientChart?.hasCompletedMeasureResponses ?? false,
      }));
      setMeasuresData(sortedMeasureData);

      setPatientShortIdWithFetchedMeasures(patientShortId);
    },
    [setNudges, setMeasuresData, setPatientShortIdWithFetchedMeasures, setPatientShortIdToHasCompletedMeasureResponses],
  );
};

/** Writes `GetPatientMeasuresByPatientShortId` results into provider Jotai state (bundles, nudges, flags). */
export function useApplyPatientMeasuresQueryResult() {
  return useSetPatientMeasuresDataCallback();
}

export function useGetMeasuresByPatientShortId(patientShortId?: string) {
  const onCompleted = useSetPatientMeasuresDataCallback();
  const existingPatientMeasuresWereFetchedFor = useAtomValue(patientShortIdWithFetchedMeasuresAtom);
  const patientMeasuresAlreadyFetched = existingPatientMeasuresWereFetchedFor === patientShortId;
  // TODO CARED-2858 remove skip logic & start returning the query once we've fixed the unnecessary re-fetching of measures
  useQuery<GetPatientMeasuresByPatientShortIdQueryData, GetPatientMeasuresByPatientShortIdVariables>(
    GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID,
    {
      context: { server: Server.MONOLITH },
      variables: { patientShortId },
      skip: !patientShortId || patientMeasuresAlreadyFetched,
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        logger.error('Unable to fetch client measures', { patientShortId }, error);
      },
      onCompleted,
    },
  );
}

export function useLazyGetMeasuresByPatientShortId() {
  const onCompleted = useSetPatientMeasuresDataCallback();
  return useLazyQuery<GetPatientMeasuresByPatientShortIdQueryData, GetPatientMeasuresByPatientShortIdVariables>(
    GET_PATIENT_MEASURES_BY_PATIENT_SHORT_ID,
    {
      context: { server: Server.MONOLITH },
      notifyOnNetworkStatusChange: true,
      onCompleted,
    },
  );
}
